'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { z } from '@repo/schema';
import { GetUserSchema } from '@repo/schema/user';
import { TEST_IDS } from '@repo/sdk/constants';
import { useQuery } from '@repo/tanstack/query';
import { Avatar, AvatarFallback, AvatarImage, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Skeleton, useCommandState } from '@repo/ui/components';
type SearchableUser = z.infer<typeof GetUserSchema>;
interface UserCommandProps {
  className?: string;
  companyId?: null | number;
  disabled?: boolean;
  onSelect: (value: {
    id: number;
    title: string;
  }) => void;
}
const getUserTitle = (user: Pick<SearchableUser, 'last_name' | 'name'>): string => {
  return [user.name, user.last_name].filter(Boolean).join(' ');
};
const UserCommandSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-2 p-2" data-sentry-component="UserCommandSkeleton" data-sentry-source-file="user-command.tsx">{Array.from({
        length: 5
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const UserCommandItem = t0 => {
  const $ = _c(32);
  const {
    onSelect,
    user
  } = t0;
  let t1;
  if ($[0] !== user) {
    t1 = getUserTitle(user);
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== onSelect || $[3] !== title || $[4] !== user.id) {
    t2 = () => onSelect({
      id: user.id,
      title
    });
    $[2] = onSelect;
    $[3] = title;
    $[4] = user.id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = `${title} ${user.id}`;
  const t4 = user.avatar?.url;
  let t5;
  if ($[6] !== t4) {
    t5 = <AvatarImage src={t4} data-sentry-element="AvatarImage" data-sentry-source-file="user-command.tsx" />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = user.name?.[0];
  const t7 = user.last_name?.[0];
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <AvatarFallback data-sentry-element="AvatarFallback" data-sentry-source-file="user-command.tsx">{t6}{t7}</AvatarFallback>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <Avatar data-sentry-element="Avatar" data-sentry-source-file="user-command.tsx">{t5}{t8}</Avatar>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== title) {
    t10 = <p>{title}</p>;
    $[14] = title;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = user.phone_formatted ?? user.phone;
  let t12;
  if ($[16] !== t11 || $[17] !== user.email) {
    t12 = [t11, user.email].filter(Boolean);
    $[16] = t11;
    $[17] = user.email;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== user.id) {
    t13 = <div className="flex gap-1">{t12.map((item, index, array) => <p key={`${user.id}-${item}`}>{item}{index < array.length - 1 && ","}</p>)}</div>;
    $[19] = t12;
    $[20] = user.id;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t13) {
    t14 = <div className="flex flex-col gap-2">{t10}{t13}</div>;
    $[22] = t10;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14 || $[26] !== t9) {
    t15 = <div className="flex items-center gap-2 px-3 py-2">{t9}{t14}</div>;
    $[25] = t14;
    $[26] = t9;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t15 || $[29] !== t2 || $[30] !== t3) {
    t16 = <CommandItem onSelect={t2} value={t3} data-sentry-element="CommandItem" data-sentry-component="UserCommandItem" data-sentry-source-file="user-command.tsx">{t15}</CommandItem>;
    $[28] = t15;
    $[29] = t2;
    $[30] = t3;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
};
const UserCommandContent = t0 => {
  const $ = _c(10);
  const {
    companyId,
    onSelect
  } = t0;
  const search = useCommandState(_temp2);
  const api = useApi();
  const t1 = companyId ?? undefined;
  let t2;
  let t3;
  if ($[0] !== api.users.list || $[1] !== search || $[2] !== t1) {
    const params = {
      "filter[company_id]": t1,
      search
    };
    t2 = useQuery;
    t3 = api.users.list.queryOptions({
      input: params
    });
    $[0] = api.users.list;
    $[1] = search;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const {
    data,
    isPending
  } = t2(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="user-command.tsx">No results found</CommandEmpty>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== data?.data || $[7] !== isPending || $[8] !== onSelect) {
    t5 = <>{t4}<div className="max-h-96 overflow-y-auto">{isPending ? <UserCommandSkeleton /> : <CommandList><CommandGroup>{data?.data?.map(user => <UserCommandItem key={user.id} onSelect={onSelect} user={user} />)}</CommandGroup></CommandList>}</div></>;
    $[6] = data?.data;
    $[7] = isPending;
    $[8] = onSelect;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
export const UserCommand = t0 => {
  const $ = _c(9);
  const {
    className,
    companyId,
    disabled,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== disabled) {
    t1 = <CommandInput autoFocus={true} data-testid={TEST_IDS.user.commandInput} disabled={disabled} placeholder="Search user" data-sentry-element="CommandInput" data-sentry-source-file="user-command.tsx" />;
    $[0] = disabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== companyId || $[3] !== onSelect) {
    t2 = <UserCommandContent companyId={companyId} onSelect={onSelect} data-sentry-element="UserCommandContent" data-sentry-source-file="user-command.tsx" />;
    $[2] = companyId;
    $[3] = onSelect;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== className || $[6] !== t1 || $[7] !== t2) {
    t3 = <Command shouldFilter={false} data-sentry-element="Command" data-sentry-component="UserCommand" data-sentry-source-file="user-command.tsx"><div className={className}>{t1}{t2}</div></Command>;
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
function _temp(_, index) {
  return <Skeleton className="h-16 w-full" key={index} />;
}
function _temp2(state) {
  return state.search;
}